<template>
  <el-dialog
    :model-value="visible"
    title="修改密码"
    width="500px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form
      style="margin-right: 30px"
      :model="form"
      :rules="rules"
      ref="formRef"
      label-width="100px"
    >
      <PhoneSmsCode
        v-model:phone="form.phone"
        v-model:smsCode="form.smsCode"
        api-url="/api/password/reset/sms"
        phone-prop="phone"
        sms-code-prop="smsCode"
        phone-placeholder="手机号"
        :phone-disabled="true"
      />
      <el-form-item label="旧密码" prop="oldPassword">
        <el-input
          v-model="form.oldPassword"
          type="password"
          placeholder="请输入旧密码"
          show-password
          prefix-icon="Lock"
          autocomplete="current-password"
        ></el-input>
      </el-form-item>

      <el-form-item label="新密码" prop="newPassword">
        <el-input
          v-model="form.newPassword"
          type="password"
          placeholder="请输入新密码（必须大于8位，包含字母和数字）"
          show-password
          prefix-icon="Lock"
          autocomplete="new-password"
        ></el-input>
      </el-form-item>

      <el-form-item label="确认密码" prop="confirmPassword">
        <el-input
          v-model="form.confirmPassword"
          type="password"
          placeholder="请再次输入新密码"
          show-password
          prefix-icon="Lock"
          autocomplete="new-password"
        ></el-input>
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" :loading="loading" @click="handleSubmit">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ref, toRefs, nextTick, onMounted } from "vue";
import { ElForm, ElMessage } from "element-plus";
import { changePassword } from "@/api/index";
import { getUserInfo as getUserInfoApi } from "@/api/index";
import PhoneSmsCode from "@/views/components/PhoneSmsCode.vue";

interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
}>();

const { visible } = toRefs(props);
const loading = ref(false);
const formRef = ref<typeof ElForm>();

const form = ref({
  phone: "",
  smsCode: "",
  oldPassword: "",
  newPassword: "",
  confirmPassword: "",
});

// 获取用户手机号
const getUserPhone = async () => {
  try {
    const res = await getUserInfoApi();
    if (res?.code === "success" && res.content) {
      const userInfo = res.content as { phone?: string };
      if (userInfo.phone) {
        form.value.phone = userInfo.phone;
      }
    }
  } catch (error) {
    console.error("获取用户信息失败:", error);
  }
};

// 新密码校验：必须大于8位，包含字母和数字
const validateNewPassword = (
  _rule: any,
  value: string,
  callback: (err?: string) => void
) => {
  if (!value) {
    return callback("请输入新密码");
  }
  if (value.length < 8) {
    return callback("密码长度至少8位");
  }
  // 必须包含字母和数字
  const hasLetter = /[a-zA-Z]/.test(value);
  const hasNumber = /\d/.test(value);
  if (!hasLetter || !hasNumber) {
    return callback("密码必须包含字母和数字");
  }
  return callback();
};

// 确认密码校验
const validateConfirmPassword = (
  _rule: any,
  value: string,
  callback: (err?: string) => void
) => {
  if (!value) {
    return callback("请再次输入新密码");
  }
  if (value !== form.value.newPassword) {
    return callback("两次输入的密码不一致");
  }
  return callback();
};

const rules = {
  oldPassword: [{ required: true, message: "请输入旧密码" }],
  phone: [{ required: true, message: "请输入手机号" }],
  smsCode: [{ required: true, message: "请输入验证码" }],
  newPassword: [{ validator: validateNewPassword }],
  confirmPassword: [{ validator: validateConfirmPassword }],
};

// 关闭弹窗
const handleClose = () => {
  emit("update:visible", false);
  // 重置表单
  formRef.value?.resetFields();
  form.value = {
    phone: "",
    smsCode: "",
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  };
};

onMounted(() => {
  getUserPhone();
});

// 提交
const handleSubmit = async () => {
  const valid = await formRef.value?.validate().catch(() => false);
  if (!valid) return;

  loading.value = true;
  try {
    const res = await changePassword({
      old_password: form.value.oldPassword,
      new_password: form.value.newPassword,
      sms_code: form.value.smsCode,
    });

    if (res?.code === "success") {
      ElMessage.success("密码修改成功，请重新登录");
      handleClose();
      // 延迟退出登录
      setTimeout(() => {
        localStorage.removeItem("userInfo");
        window.location.href = "/login";
      }, 1000);
    } else {
      ElMessage.error(res?.message || "密码修改失败");
    }
  } catch (error) {
    ElMessage.error("密码修改失败，请稍后重试");
  } finally {
    loading.value = false;
  }
};
</script>

<style lang="scss" scoped>
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
